import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FullTaxInvoiceDisplay, InvoiceHeaderResponse } from '../../../../../../core/models/invoice.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { VatSettingService } from '../../../../../../core/services/vat-setting.service';
import { ToastService } from 'angular-toastify';
import * as html2canvas from 'html2canvas';
import jsPDF from 'jspdf';


@Component({
  selector: 'app-modal-invoice-fulltax',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal-invoice-fulltax.component.html',
  styleUrls: ['./modal-invoice-fulltax.component.css']
})
export class ModalInvoiceFulltaxComponent implements OnInit {
  @Input() id: number = 0;
  @Output() close = new EventEmitter<void>();

  invoiceDisplay: FullTaxInvoiceDisplay | null = null;
  isLoading = false;
  today: Date = new Date();
  isCopy: boolean = false; // State to control Original/Copy label

  // For PSS Group hardcoded info
  companyInfo = {
    nameTh: 'บริษัท พีเอสเอส กรุ๊ป (ประเทศไทย) จำกัด',
    nameEn: 'PSS GROUP (THAILAND) CO.,LTD.',
    addressTh: 'สำนักงานใหญ่ : 45/4 หมู่ที่ 5 ต.บางเมือง อ.เมืองสมุทรปราการ จ.สมุทรปราการ 10270',
    addressEn: 'Head Office : 45/4 Moo5, Bang Mueang , Mueang-Samut Prakan, Samut Prakan 10270',
    tel: 'โทร. 02-0962255',
    taxId: '0115560017379'
  };

  bahtTextAmount: string = '';

  isPdfGenerating = false;

  readonly vatSetting = inject(VatSettingService);

  constructor(
    private apiService: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    if (this.id) {
      this.getData();
    } else {
      const routeId = this.route.snapshot.paramMap.get('id');
      if (routeId) {
        this.id = +routeId;
        this.getData();
      }
    }
  }

  getData() {
    this.isLoading = true;
    this.apiService.getCityParkingInvoiceById(this.id).subscribe({
      next: (res) => {
        this.isLoading = false;
        // Map response to display model
        this.mapToDisplayModel(res);
      },
      error: (err) => {
        console.error('Error fetching invoice:', err);
        this.toast.error('ไม่สามารถดึงข้อมูลใบแจ้งหนี้ได้');
        this.isLoading = false;
      }
    });
  }

  onClose() {
    this.close.emit();
  }

  print() {
    window.print();
  }

  private waitForRender(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  async downloadPDF() {
    this.isPdfGenerating = true;

    const element = document.querySelector('.a4-page') as HTMLElement;
    if (!element) {
      this.toast.error('ไม่พบเนื้อหาสำหรับสร้าง PDF');
      this.isPdfGenerating = false;
      return;
    }

    try {
      // Original
      this.isCopy = false;
      await this.waitForRender();
      await this.generateAndSavePDF(element, 'original');

      // Copy
      this.isCopy = true;
      await this.waitForRender();
      await this.generateAndSavePDF(element, 'copy');

      this.toast.success('ดาวน์โหลดไฟล์ PDF เรียบร้อยแล้ว');
    } catch (err) {
      console.error(err);
      this.toast.error('สร้าง PDF ไม่สำเร็จ');
    } finally {
      this.isCopy = false;
      this.isPdfGenerating = false;
    }
  }


  private async generateAndSavePDF(element: HTMLElement, suffix: string) {
    const canvas = await html2canvas.default(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      onclone: (doc) => {
        // กัน oklch หลุดมาอีกชั้น
        doc.querySelectorAll('*').forEach((el: any) => {
          const style = getComputedStyle(el);

          if (style.color?.includes('oklch')) {
            el.style.color = '#002060';
          }
          if (style.backgroundColor?.includes('oklch')) {
            el.style.backgroundColor = '#ffffff';
          }
          if (style.borderColor?.includes('oklch')) {
            el.style.borderColor = '#3e8ec2';
          }
        });
      }
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`invoice-${this.invoiceDisplay?.invoiceNo}-${suffix}.pdf`);
  }


  // --- BahtText Helper ---
  getBahtText(num: number): string {
    if (!num) return 'ศูนย์บาทถ้วน';

    // Formatting number
    num = Number(num.toFixed(2));

    const textNumArr = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
    const textDigitArr = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];

    let bahtText = '';

    const parts = num.toString().split('.');
    const baht = parts[0];
    const satang = parts.length > 1 ? parts[1] : '00';

    // Process Baht
    if (Number(baht) > 0) {
      const bahtLen = baht.length;
      for (let i = 0; i < bahtLen; i++) {
        const digit = Number(baht.charAt(i));
        const pos = bahtLen - i - 1;

        if (digit !== 0) {
          if (pos % 6 === 1 && digit === 1 && bahtLen > 1 && i > 0) { // Ed-Sib
            bahtText += 'เอ็ด';
          } else if (pos % 6 === 1 && digit === 2) { // Yee-Sib
            bahtText += 'ยี่';
          } else if (pos % 6 !== 1 || digit !== 1) {
            bahtText += textNumArr[digit];
          }

          bahtText += textDigitArr[pos % 6];
        } else if (pos % 6 === 0 && pos > 0) { // Million
          bahtText += textDigitArr[6];
        }
      }
      bahtText += 'บาท';
    }

    // Process Satang
    if (Number(satang) > 0) {
      const satangLen = satang.length;
      if (satangLen === 1) { // e.g. .5 -> 50 satang
        // Handled by toFixed(2) but just safety
      }

      for (let i = 0; i < satangLen; i++) {
        const digit = Number(satang.charAt(i));
        const pos = satangLen - i - 1;

        if (digit !== 0) {
          if (pos === 0 && digit === 1 && satangLen > 1) {
            bahtText += 'เอ็ด';
          } else if (pos === 1 && digit === 2) {
            bahtText += 'ยี่';
          } else if (pos === 1 && digit === 1) {
            // Skip 'Nueng' for Sib
          } else {
            bahtText += textNumArr[digit];
          }

          if (pos === 1) bahtText += 'สิบ';
        }
      }
      bahtText += 'สตางค์';
    } else {
      bahtText += 'ถ้วน';
    }

    return bahtText;
  }

  private mapToDisplayModel(res: InvoiceHeaderResponse) {
    const totalAmount = (res.fee || 0) + (res.vat || 0);

    // Format address
    let address = '';
    if (res.cityParkingAddress) {
      address = res.cityParkingAddress.address || '';
      if (res.cityParkingAddress.subDistrict) address += ` ต.${res.cityParkingAddress.subDistrict}`;
      if (res.cityParkingAddress.district) address += ` อ.${res.cityParkingAddress.district}`;
      if (res.cityParkingAddress.province) address += ` จ.${res.cityParkingAddress.province}`;
      if (res.cityParkingAddress.zipCode) address += ` ${res.cityParkingAddress.zipCode}`;
    } else {
      address = '- ไม่ระบุที่อยู่ -';
    }

    // Format Branch
    let branch = '';
    if (res.cityParkingAddress) {
      if (!res.cityParkingAddress.branchNo || res.cityParkingAddress.branchNo === 'สำนักงานใหญ่') {
        branch = 'สำนักงานใหญ่';
      } else {
        branch = `สาขาที่ ${res.cityParkingAddress.branchNo}`;
      }
    }

    this.invoiceDisplay = {
      customerName: res.customerName,
      customerAddress: address,
      customerTaxId: res.cityParkingAddress?.taxId || '-',
      customerBranch: branch,
      invoiceNo: res.invoiceNo,
      invoiceDate: res.invoiceDate || new Date(),
      items: res.invoiceDetailResponses.map(item => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        pricePerUnit: item.fee,
        totalPrice: item.fee * item.quantity
      })),
      subTotal: res.fee,
      vat: res.vat,
      grandTotal: totalAmount,
      bahtText: this.getBahtText(totalAmount),
      remark: res.remark,
      signatures: {
        customerDate: '.................../.................../...................',
        issuerDate: '.................../.................../...................'
      }
    };
  }
}
